/**
 * product-card-image-fix.js
 * ---------------------------------------------------------------------------------------------
 * Fixes uneven product image heights in every product GRID, SLIDER and LIST of the theme.
 *
 * For each group of product cards (one grid / one slider / one list) the script measures the
 * aspect ratio of every product image and applies the tallest one to all cards of that group:
 *   - standard cards  -> --ratio-percent on .card__inner.ratio (drives the media box height)
 *   - wishlist/compare-> aspect-ratio on .card__media1
 * Images use object-fit: cover, so every card ends up with exactly the same image height.
 *
 * Only product cards are recognised (a card must live in .product-grid / .product-card-wrapper
 * or contain a product price). Blog article cards and collection cards are skipped, and hero /
 * header sliders never contain product cards, so those sections are left untouched.
 * ---------------------------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var UNIT_SELECTOR = '.card__inner.ratio, .card__media1';

  /* Ratio (width / height) of the first image of a media box, 0 when unknown. */
  function getImageRatio(unit) {
    var img = unit.querySelector('img');
    if (!img) return 0;

    var width = parseFloat(img.getAttribute('width'));
    var height = parseFloat(img.getAttribute('height'));

    if (!width || !height) {
      width = img.naturalWidth;
      height = img.naturalHeight;
    }
    if (!width || !height) return 0;

    var ratio = width / height;
    return ratio > 0.05 && ratio < 20 ? ratio : 0;
  }

  /* Is this media box a product card? (skips article cards, collection cards, ...) */
  function isProductUnit(unit) {
    if (!unit.querySelector('img')) return false;

    var card = unit.closest('.card');
    if (!card) return false;
    if (card.classList.contains('article-card')) return false;

    var wrapper = card.closest('.card-wrapper');
    if (wrapper && wrapper.classList.contains('collection-card-wrapper')) return false;

    /* wishlist / compare product lists */
    if (unit.classList.contains('card__media1')) return true;

    /* product grids, product sliders and normal product cards (they render a price) */
    return Boolean(
      unit.closest('.product-grid') ||
      unit.closest('.product-card-wrapper') ||
      card.querySelector('.price')
    );
  }

  function normalizeProductImages() {
    var found = document.querySelectorAll(UNIT_SELECTOR);
    var units = [];
    var i;

    for (i = 0; i < found.length; i++) {
      if (isProductUnit(found[i])) units.push(found[i]);
    }
    if (units.length < 2) return;

    /* How many product units live under each ancestor? */
    var counts = new Map();
    var chains = [];

    units.forEach(function (unit) {
      var chain = [];
      var parent = unit.parentElement;
      while (parent && parent !== document.body && parent !== document.documentElement) {
        chain.push(parent);
        counts.set(parent, (counts.get(parent) || 0) + 1);
        parent = parent.parentElement;
      }
      chains.push(chain);
    });

    /* Group every card by the closest container that holds more than one card
       (the grid, the slider track or the list wrapper). */
    var groups = new Map();

    units.forEach(function (unit, index) {
      var chain = chains[index];
      var group = null;
      for (var j = 0; j < chain.length; j++) {
        if (counts.get(chain[j]) >= 2) {
          group = chain[j];
          break;
        }
      }
      if (!group) return; /* single product card: nothing to match */
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group).push(unit);
    });

    groups.forEach(function (group) {
      /* the tallest image = the smallest width / height ratio */
      var tallest = 0;
      group.forEach(function (unit) {
        var ratio = getImageRatio(unit);
        if (ratio > 0 && (!tallest || ratio < tallest)) tallest = ratio;
      });
      if (!tallest) return;

      var percent = Math.round((100 / tallest) * 100) / 100;

      group.forEach(function (unit) {
        if (unit.classList.contains('card__media1')) {
          unit.style.setProperty('aspect-ratio', String(tallest));
        } else {
          unit.style.setProperty('--ratio-percent', percent + '%');
        }
      });
    });
  }

  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    window.setTimeout(function () {
      scheduled = false;
      normalizeProductImages();
    }, 150);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', normalizeProductImages);
  } else {
    normalizeProductImages();
  }

  /* images that report their real size only after loading */
  window.addEventListener('load', schedule);

  /* theme editor section reloads (shopify:section:load) */
  document.addEventListener('shopify:section:load', schedule);

  /* dynamically injected product cards: filters, tabs, recently viewed, wishlist, ... */
  if (window.MutationObserver && document.body) {
    new MutationObserver(schedule).observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /* manual re-run from the browser console: productCardImageFix.normalize() */
  window.productCardImageFix = { normalize: normalizeProductImages };
})();
